import React from 'react';
import { BrowserRouter as Router, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { jwtDecode } from 'jwt-decode';
import LoginPage from './components/LoginPage';
import RegisterPage from './components/RegisterPage';
import ProtectedRoute from './components/ProtectedRoute';
import Navbar from './components/NavBar';
import ProfilePage from './components/ProfilePage';
import SettingsPage from './components/SettingsPage';
import Administration from './components/AdministrationPage';
import ProtectedAdminRoute from './components/ProtectedAdminRoute';
import FooterPage from './components/FooterPage';
import DashBoardPage from './components/DashBoardPage';
import VehiculePage from './components/VehiculePage';
import ReleverPage from './components/ReleverPage';
import SimulationPage from './components/SimulationPage';
import UpdatePage from './components/UpdatePage';
import EntretienPage from './components/EntretienPage';
import StatisticsPage from './components/StatisticsPage';
import SubscriptionPage from './components/SubscriptionPage';
import PublicOffersPage from './components/PublicOffersPage';
import GaragePage from './components/GaragePage';
import { usePrivacy } from './privacy/PrivacyContext';

//import NotProtectedRoute from './components/NotProtectedRoute';

const NameApp = process.env.REACT_APP_NAME + " " + process.env.REACT_APP_VER;

function AppLayout({ children }) {
  return (
    <>
      <Navbar />
      <div className="vehicle-app-shell min-h-screen lg:pl-64">
        <div className="px-4 pb-10 pt-6 sm:px-6 lg:px-8">
          {children}
        </div>
        <FooterPage />
      </div>
    </>
  );
}

const routesMeta = {
  "/login": {
    title: `Connexion - ${NameApp}`,
    description: `Connectez-vous pour accéder à votre compte ${NameApp}.`,
  },
  "/register": {
    title: `Inscription - ${NameApp}`,
    description: `Créez un compte pour accéder à ${NameApp}.`,
  },
  "/offres": {
    title: `Offres et droits - ${NameApp}`,
    description: `Comparez les comptes Utilisateur, Premium et Professionnel de ${NameApp}.`,
  },
  "/profile": {
    title: `Profil - ${NameApp}`,
    description: "Bienvenue sur votre profil.",
  },
  "/settings": {
    title: `Paramètres - ${NameApp}`,
    description: "Bienvenue sur votre Paramètres.",
  },
  "/administration": {
    title: `Administration - ${NameApp}`,
    description: `Gérez les utilisateurs et les paramètres administratifs de ${NameApp}.`,
  },
  "/updates": {
    title: `Mises à jour - ${NameApp}`,
    description: `Consultez l'historique des mises à jour de ${NameApp}.`,
  },
  "/entretien": {
    title: `Entretien - ${NameApp}`,
    description: `Planifiez et suivez les entretiens de vos véhicules dans ${NameApp}.`,
  },
  "/garages": {
    title: `Garages - ${NameApp}`,
    description: `Gérez vos garages habituels et les immobilisations de vos véhicules dans ${NameApp}.`,
  },
  "/abonnement": {
    title: `Abonnement - ${NameApp}`,
    description: "Gérez l’abonnement et les droits de votre compte.",
  },
  "/statistiques": {
    title: `Statistiques - ${NameApp}`,
    description: `Consultez les statistiques globales de vos véhicules dans ${NameApp}.`,
  },
  // Ajoute d'autres routes ici...
};

function MetaUpdater() {
  const location = useLocation();
  const meta = routesMeta[location.pathname] || {
    title: `${NameApp}`,
    description: "Bienvenue sur VEHICLE, votre application de registre de véhicule privée.",
  };

  return (
    <Helmet>
      <title>{meta.title}</title>
      <meta name="description" content={meta.description} />
    </Helmet>
  );
}

function PrivacyBlockedRoute({ children }) {
  const { isPrivacyMode } = usePrivacy();
  return isPrivacyMode ? <Navigate to="/dashboard" replace /> : children;
}

function OffersRoute() {
  const token = localStorage.getItem('token');
  let authenticated = false;
  if (token && token !== 'undefined') {
    try {
      authenticated = Number(jwtDecode(token)?.exp || 0) >= Date.now() / 1000;
    } catch (error) {
      authenticated = false;
    }
  }
  return authenticated ? (
    <AppLayout><PublicOffersPage authenticated /></AppLayout>
  ) : (
    <PublicOffersPage />
  );
}

export default function App() {
  return (
    <Router>
      <MetaUpdater />
      <Routes>
        {/* Pages sans Navbar */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/offres" element={<OffersRoute />} />

        {/* Pages avec Navbar */}
        <Route
          path="/profile"
          element={
            <ProtectedRoute>
              <AppLayout>
                <ProfilePage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <PrivacyBlockedRoute>
                <AppLayout>
                  <SettingsPage />
                </AppLayout>
              </PrivacyBlockedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/administration"
          element={
            <ProtectedAdminRoute>
              <PrivacyBlockedRoute>
                <AppLayout>
                  <Administration />
                </AppLayout>
              </PrivacyBlockedRoute>
            </ProtectedAdminRoute>
          }
        />
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <AppLayout>
                <DashBoardPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/vehicule"
          element={
            <ProtectedRoute>
              <AppLayout>
                <VehiculePage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/relever"
          element={
            <ProtectedRoute>
              <PrivacyBlockedRoute>
                <AppLayout>
                  <ReleverPage />
                </AppLayout>
              </PrivacyBlockedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/simulation"
          element={
            <ProtectedRoute>
              <AppLayout>
                <SimulationPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/entretien"
          element={
            <ProtectedRoute>
              <AppLayout>
                <EntretienPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/garages"
          element={
            <ProtectedRoute>
              <PrivacyBlockedRoute>
                <AppLayout>
                  <GaragePage />
                </AppLayout>
              </PrivacyBlockedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/statistiques"
          element={
            <ProtectedRoute>
              <AppLayout>
                <StatisticsPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/abonnement"
          element={
            <ProtectedRoute>
              <AppLayout>
                <SubscriptionPage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        <Route
          path="/updates"
          element={
            <ProtectedRoute>
              <AppLayout>
                <UpdatePage />
              </AppLayout>
            </ProtectedRoute>
          }
        />
        {/* Page d'accueil */}
        <Route path="/" element={
            <ProtectedRoute>
              <AppLayout>
                <DashBoardPage />
              </AppLayout>
            </ProtectedRoute>
          } />
      </Routes>
    </Router>
  );
}
